import DOMPurify from 'dompurify';
import { marked } from 'marked';

const enforceExternalLinkPolicy = (container) => {
    const links = container.querySelectorAll('a[href]');

    for (const link of links) {
        const href = link.getAttribute('href')?.trim();
        if (!href || href.startsWith('#') || href.startsWith('/')) {
            continue;
        }

        try {
            const resolved = new URL(href, window.location.origin);
            const isExternalHttpLink = (resolved.protocol === 'http:' || resolved.protocol === 'https:') && resolved.origin !== window.location.origin;

            if (isExternalHttpLink) {
                link.setAttribute('target', '_blank');
                link.setAttribute('rel', 'noopener noreferrer nofollow');
            }
        } catch {
            continue;
        }
    }
};

const renderMarkdownSafely = (rawText) => {
    const parsedMarkdown = marked.parse(rawText ?? '', {
        gfm: true,
        breaks: true
    });

    const sanitizedHtml = DOMPurify.sanitize(parsedMarkdown, {
        USE_PROFILES: {
            html: true
        }
    });

    const wrapper = document.createElement('div');
    wrapper.innerHTML = sanitizedHtml;
    enforceExternalLinkPolicy(wrapper);

    return wrapper.innerHTML;
};

const renderAssistantMarkdown = (messageTextElement, rawText) => {
    if (typeof rawText === 'string') {
        messageTextElement.dataset.chatRawMarkdown = rawText;
    }

    const content = rawText?.trim();
    if (!content) {
        messageTextElement.textContent = 'I could not generate a response right now. Please try again.';
        return;
    }

    try {
        messageTextElement.innerHTML = renderMarkdownSafely(rawText);
    } catch {
        messageTextElement.textContent = rawText;
    }
};

const renderExistingAssistantMarkdown = (historyElement) => {
    if (!historyElement) {
        return;
    }

    const existingAssistantMessages = historyElement.querySelectorAll('[data-chat-role="assistant"][data-chat-message-text]');

    for (const messageText of existingAssistantMessages) {
        const preservedRawMarkdown = messageText.dataset.chatRawMarkdown;
        const sourceText = typeof preservedRawMarkdown === 'string' && preservedRawMarkdown.length > 0
            ? preservedRawMarkdown
            : (messageText.textContent ?? '');

        renderAssistantMarkdown(messageText, sourceText);
    }
};

const copyTextToClipboard = async (text) => {
    if (!text) {
        return false;
    }

    try {
        if (navigator.clipboard?.writeText) {
            await navigator.clipboard.writeText(text);
            return true;
        }
    } catch {
        // Fall through to legacy copy strategy.
    }

    try {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.setAttribute('readonly', 'true');
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.append(textArea);
        textArea.select();
        const copied = document.execCommand('copy');
        textArea.remove();
        return copied;
    } catch {
        return false;
    }
};

function initialiseRenderedChatMessages() {
    const historyElement = document.getElementById('chat-history');
    if (!historyElement) {
        return;
    }

    renderExistingAssistantMarkdown(historyElement);

    for (const copyButton of historyElement.querySelectorAll('.chat-summary-copy-button[data-copy-text]')) {
        if (copyButton.dataset.copyInitialised === 'true') {
            continue;
        }

        copyButton.dataset.copyInitialised = 'true';

        copyButton.addEventListener('click', async () => {
            const copied = await copyTextToClipboard(copyButton.dataset.copyText ?? '');
            if (!copied) {
                return;
            }

            const icon = copyButton.querySelector('i');
            if (icon) {
                icon.className = 'bi bi-check2';
            }
            copyButton.setAttribute('aria-label', 'Message copied');
            copyButton.title = 'Message copied';

            window.setTimeout(() => {
                const resetIcon = copyButton.querySelector('i');
                if (resetIcon) {
                    resetIcon.className = 'bi bi-copy';
                }
                copyButton.setAttribute('aria-label', 'Copy message text');
                copyButton.title = 'Copy message text';
            }, 1600);
        });
    }
}

function updateFlagButton(isFlagged) {
    const button = document.getElementById('chat-flag-button');
    const form = document.getElementById('chat-form');
    if (!button || !form) return;
    if (typeof isFlagged === 'boolean') {
        button.setAttribute('aria-pressed', String(isFlagged));
    }
    const flagged = button.getAttribute('aria-pressed') === 'true';
    button.title = flagged ? 'Remove flag' : 'Flag for later review';
    button.setAttribute('aria-label', button.title);
    button.querySelector('i').className = flagged ? 'bi bi-flag-fill' : 'bi bi-flag';
    button.disabled = !document.getElementById('chat-conversation-id')?.value ||
        form.dataset.isSubmitting === 'true' || form.dataset.isFlagging === 'true' ||
        form.dataset.isLoadingConversation === 'true';
}

function initialiseConversationFlagging() {
    const button = document.getElementById('chat-flag-button');
    const form = document.getElementById('chat-form');
    if (!button || !form) return;
    updateFlagButton();
    button.addEventListener('click', async () => {
        const conversationId = document.getElementById('chat-conversation-id').value;
        if (!conversationId || button.disabled) return;
        form.dataset.isFlagging = 'true';
        updateFlagButton();
        const error = document.getElementById('chat-error');
        let saved = false;
        try {
            const response = await fetch(form.dataset.flagUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    RequestVerificationToken: form.querySelector('input[name="__RequestVerificationToken"]').value
                },
                body: new URLSearchParams({ conversationId })
            });
            if (!response.ok) throw new Error('Flag update failed');
            const result = await response.json();
            saved = true;
            if (document.getElementById('chat-conversation-id').value === conversationId) {
                updateFlagButton(result.isFlagged);
            }
            error.classList.add('d-none');

            // Reload history so new conversations and changed date groups are included.
            const historyResponse = await fetch(form.dataset.historyUrl);
            if (!historyResponse.ok) throw new Error('History refresh failed');
            const documentFragment = new DOMParser().parseFromString(await historyResponse.text(), 'text/html');
            const scroll = document.querySelector('.chat-sidebar-history-scroll');
            const expandedIds = Array.from(scroll.querySelectorAll('.chat-history-group-body.show .history-conversation-item'))
                .map(item => item.dataset.conversationId);
            const selectedId = document.getElementById('chat-conversation-id').value === conversationId ?
                String(result.id) : scroll.querySelector('.history-conversation-link-selected')?.closest('li').dataset.conversationId;
            scroll.innerHTML = documentFragment.querySelector('.chat-sidebar-history-scroll').innerHTML;
            scroll.querySelectorAll('.history-conversation-item').forEach(item => {
                const selected = item.dataset.conversationId === selectedId;
                if (selected) {
                    const link = item.querySelector('a');
                    link.classList.add('history-conversation-link-selected');
                    link.setAttribute('aria-current', 'page');
                }
                if (selected || expandedIds.includes(item.dataset.conversationId)) {
                    const group = item.closest('.chat-history-group-body');
                    group.classList.add('show');
                    const toggle = document.getElementById(group.getAttribute('aria-labelledby')).querySelector('button');
                    toggle.classList.remove('collapsed');
                    toggle.setAttribute('aria-expanded', 'true');
                }
            });
        } catch {
            error.textContent = saved ? 'Flag saved, but history could not refresh. Reload the page to see it.' :
                'Unable to update the flag. Please try again.';
            error.classList.remove('d-none');
        } finally {
            form.dataset.isFlagging = 'false';
            updateFlagButton();
        }
    });
}

function initialiseChatStreaming() {
    const chatForm = document.getElementById('chat-form');
    if (!chatForm) {
        return;
    }

    const historyElement = document.getElementById('chat-history');
    const messageInput = document.getElementById('chat-message');
    const sendButton = document.getElementById('chat-send-button');
    const summariseButton = document.getElementById('chat-summarise-button');
    const conversationInput = document.getElementById('chat-conversation-id');
    const errorElement = document.getElementById('chat-error');
    const userTemplate = document.getElementById('chat-message-template-user');
    const assistantTemplate = document.getElementById('chat-message-template-assistant');
    const antiForgeryToken = chatForm.querySelector('input[name="__RequestVerificationToken"]');
    const streamUrl = chatForm.dataset.streamUrl;

    if (!historyElement || !messageInput || !sendButton || !conversationInput || !errorElement || !userTemplate || !assistantTemplate || !antiForgeryToken || !streamUrl) {
        return;
    }

    const existingUserMessages = historyElement.querySelectorAll('[data-chat-role="user"][data-chat-message-text]');
    chatForm.dataset.hasSentMessage = existingUserMessages.length > 0 ? 'true' : 'false';

    const currentTimestamp = () => {
        const date = new Date();
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-GB', { month: 'short' });
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${day} ${month} ${year}, ${hours}:${minutes}`;
    };

    const scrollHistoryToBottom = () => {
        historyElement.scrollTop = historyElement.scrollHeight;
    };

    const setError = (message) => {
        errorElement.textContent = message;
        errorElement.classList.remove('d-none');
    };

    const clearError = () => {
        errorElement.textContent = '';
        errorElement.classList.add('d-none');
    };

    const setSummaryCopyButtonState = (button, text) => {
        if (!button) {
            return;
        }

        const hasText = !stringIsNullOrWhiteSpace(text);
        button.disabled = !hasText;
    };

    const setSummaryCopyButtonVisibility = (button, isVisible) => {
        if (!button) {
            return;
        }

        button.classList.toggle('d-none', !isVisible);
    };

    const setSummaryCopyButtonCopied = (button, copied) => {
        if (!button) {
            return;
        }

        const icon = button.querySelector('i');
        if (icon) {
            icon.className = copied ? 'bi bi-check2' : 'bi bi-copy';
        }

        button.setAttribute('aria-label', copied ? 'Summary copied' : 'Copy summary text');
        button.title = copied ? 'Summary copied' : 'Copy summary text';
    };

    const stringIsNullOrWhiteSpace = (value) => !value || !value.trim();

    const setFormBusy = (isBusy) => {
        const hasSentMessage = chatForm.dataset.hasSentMessage === 'true';
        chatForm.dataset.isSubmitting = isBusy ? 'true' : 'false';
        chatForm.setAttribute('aria-busy', isBusy ? 'true' : 'false');
        messageInput.disabled = isBusy;
        sendButton.disabled = isBusy;
        updateFlagButton();

        if (summariseButton) {
            summariseButton.disabled = isBusy || !hasSentMessage;
        }
    };

    const createMessageElements = ({ role, text, timestamp, withThinkingIcon, thinkingLabel = 'Compiling Response', thinkingIconClass = 'bi bi-hourglass-split chat-thinking-icon', isSummaryMessage = false, useThinkingEllipsis = false }) => {
        const template = role === 'user' ? userTemplate : assistantTemplate;
        const messageWrapper = template.content.firstElementChild.cloneNode(true);
        const messageText = messageWrapper.querySelector('[data-chat-message-text]');
        const messageTimestamp = messageWrapper.querySelector('[data-chat-message-timestamp]');
        const thinkingWrap = messageWrapper.querySelector('[data-chat-thinking-wrap]');
        const thinkingLabelElement = messageWrapper.querySelector('[data-chat-thinking-label]');
        const thinkingIconElement = messageWrapper.querySelector('[data-chat-thinking-icon]');
        const messageArticle = messageWrapper.querySelector('article');
        let summaryCopyButton = null;

        if (!messageText || !messageTimestamp || !messageArticle) {
            return null;
        }

        messageText.textContent = text;
        messageTimestamp.textContent = timestamp;

        if (isSummaryMessage && role === 'assistant') {
            messageWrapper.classList.add('chat-summary-message-wrapper');
            messageArticle.classList.add('chat-bubble-summary');

            summaryCopyButton = document.createElement('button');
            summaryCopyButton.type = 'button';
            summaryCopyButton.className = 'chat-summary-copy-button';
            summaryCopyButton.setAttribute('aria-label', 'Copy summary text');
            summaryCopyButton.title = 'Copy summary text';
            summaryCopyButton.innerHTML = '<i class="bi bi-copy" aria-hidden="true"></i>';

            setSummaryCopyButtonState(summaryCopyButton, text);
            setSummaryCopyButtonVisibility(summaryCopyButton, !withThinkingIcon);

            summaryCopyButton.addEventListener('click', async () => {
                const summaryText = summaryCopyButton.dataset.copyText ?? messageText.textContent?.trim() ?? '';
                const copied = await copyTextToClipboard(summaryText);

                if (!copied) {
                    return;
                }

                setSummaryCopyButtonCopied(summaryCopyButton, true);
                window.setTimeout(() => {
                    setSummaryCopyButtonCopied(summaryCopyButton, false);
                }, 1600);
            });

            messageArticle.append(summaryCopyButton);
        }

        if (thinkingWrap) {
            if (withThinkingIcon) {
                thinkingWrap.classList.remove('d-none');

                if (thinkingLabelElement) {
                    thinkingLabelElement.textContent = thinkingLabel;

                    if (useThinkingEllipsis) {
                        thinkingLabelElement.classList.add('chat-thinking-label-ellipsis');
                    }
                }

                if (thinkingIconElement) {
                    thinkingIconElement.className = thinkingIconClass;
                }
            } else {
                thinkingWrap.remove();
            }
        }

        return {
            messageWrapper,
            messageText,
            messageTimestamp,
            messageArticle,
            summaryCopyButton
        };
    };

    const removeThinkingState = (messageArticle) => {
        const thinkingWrap = messageArticle.querySelector('.chat-thinking-wrap');
        if (thinkingWrap) {
            thinkingWrap.remove();
        }
    };

    const processStream = async (response, assistantElements) => {
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let chunkBuffer = '';
        let receivedChunkContent = false;
        let assistantRawText = '';
        let receivedComplete = false;

        const parseStreamEvent = (payload) => {
            try {
                return JSON.parse(payload);
            } catch {
                removeThinkingState(assistantElements.messageArticle);
                if (!assistantElements.messageText.textContent.trim()) {
                    assistantElements.messageText.textContent = 'I could not generate a response right now. Please try again.';
                }
                setError('I could not read the chat response. Please try again.');
                return null;
            }
        };

        const handleStreamEvent = (streamEvent) => {
            const eventType = streamEvent.Type;
            const eventContent = streamEvent.Content;
            const eventConversationId = streamEvent.ConversationId;
            const eventTimestamp = streamEvent.Timestamp;
            const eventError = streamEvent.Error;

            if (eventType === 'chunk' && typeof eventContent === 'string') {
                receivedChunkContent = true;
                assistantRawText += eventContent;
                assistantElements.messageText.textContent += eventContent;
                setSummaryCopyButtonState(assistantElements.summaryCopyButton, assistantElements.messageText.textContent);
            }

            if (eventType === 'complete') {
                receivedComplete = true;

                if (typeof eventConversationId === 'string') {
                    conversationInput.value = eventConversationId;
                }

                if (typeof eventTimestamp === 'string') {
                    assistantElements.messageTimestamp.textContent = eventTimestamp;
                }

                if (!receivedChunkContent && !assistantElements.messageText.textContent.trim()) {
                    assistantElements.messageText.textContent = 'I could not generate a response right now. Please try again.';
                }

                renderAssistantMarkdown(assistantElements.messageText, assistantRawText || assistantElements.messageText.textContent);
                if (assistantElements.summaryCopyButton && assistantRawText) {
                    assistantElements.summaryCopyButton.dataset.copyText = assistantRawText;
                }
                setSummaryCopyButtonState(assistantElements.summaryCopyButton, assistantElements.messageText.textContent);
            }

            if (eventType === 'error') {
                removeThinkingState(assistantElements.messageArticle);
                if (!assistantElements.messageText.textContent.trim()) {
                    assistantElements.messageText.textContent = 'I could not complete that request. Please try again.';
                }
                setError(eventError || 'I could not complete that request. Please try again.');
                return true;
            }

            return false;
        };

        const processLines = () => {
            const lines = chunkBuffer.split('\n');
            chunkBuffer = lines.pop() ?? '';

            for (const line of lines) {
                const payload = line.trim();
                if (!payload) {
                    continue;
                }

                const streamEvent = parseStreamEvent(payload);
                if (!streamEvent) {
                    return true;
                }

                const hasError = handleStreamEvent(streamEvent);
                if (hasError) {
                    return true;
                }
            }

            return false;
        };

        while (true) {
            const { done, value } = await reader.read();

            if (done) {
                break;
            }

            chunkBuffer += decoder.decode(value, { stream: true });
            const hasError = processLines();
            if (hasError) {
                return false;
            }

            scrollHistoryToBottom();
        }

        chunkBuffer += decoder.decode();

        if (chunkBuffer.trim()) {
            const trailingPayload = chunkBuffer.trim();
            const streamEvent = parseStreamEvent(trailingPayload);
            if (!streamEvent) {
                return false;
            }

            const hasError = handleStreamEvent(streamEvent);
            if (hasError) {
                return false;
            }
        }

        if (!receivedComplete) {
            renderAssistantMarkdown(assistantElements.messageText, assistantRawText || assistantElements.messageText.textContent);
            if (assistantElements.summaryCopyButton && assistantRawText) {
                assistantElements.summaryCopyButton.dataset.copyText = assistantRawText;
            }
        }

        removeThinkingState(assistantElements.messageArticle);
        setSummaryCopyButtonState(assistantElements.summaryCopyButton, assistantElements.messageText.textContent);
        setSummaryCopyButtonVisibility(assistantElements.summaryCopyButton, !assistantElements.summaryCopyButton?.disabled);

        return true;
    };

    renderExistingAssistantMarkdown(historyElement);
    setFormBusy(false);

    const submitMessage = async (message, clearInputAfterSubmit, isSummaryRequest = false) => {
        if (chatForm.dataset.isSubmitting === 'true') {
            return false;
        }

        if (!isSummaryRequest && !message) {
            setError('Please enter a message.');
            return false;
        }

        clearError();

        if (!isSummaryRequest) {
            const userElements = createMessageElements({
                role: 'user',
                text: message,
                timestamp: currentTimestamp(),
                withThinkingIcon: false
            });

            if (!userElements) {
                setError('I could not render the chat message. Please refresh and try again.');
                return false;
            }

            historyElement.append(userElements.messageWrapper);
            chatForm.dataset.hasSentMessage = 'true';
        }

        const assistantElements = createMessageElements({
            role: 'assistant',
            text: '',
            timestamp: currentTimestamp(),
            withThinkingIcon: true,
            thinkingLabel: isSummaryRequest ? 'Summarising' : 'Thinking',
            thinkingIconClass: isSummaryRequest ? 'bi bi-card-text chat-thinking-icon-static' : 'bi bi-hourglass-split chat-thinking-icon',
            isSummaryMessage: isSummaryRequest,
            useThinkingEllipsis: isSummaryRequest
        });

        if (!assistantElements) {
            setError('I could not render the chat response. Please refresh and try again.');
            return false;
        }

        historyElement.append(assistantElements.messageWrapper);
        scrollHistoryToBottom();

        setFormBusy(true);

        if (clearInputAfterSubmit) {
            messageInput.value = '';
            messageInput.style.height = 'auto';
        }

        try {
            const response = await fetch(streamUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    RequestVerificationToken: antiForgeryToken.value
                },
                body: JSON.stringify({
                    message: isSummaryRequest ? null : message,
                    conversationId: conversationInput.value || null,
                    isSummaryRequest
                })
            });

            if (!response.ok || !response.body) {
                throw new Error('Invalid stream response.');
            }

            const contentType = response.headers.get('content-type') || '';
            if (!contentType.toLowerCase().includes('application/x-ndjson')) {
                throw new Error('Unexpected stream content type.');
            }

            return await processStream(response, assistantElements);
        } catch (error) {
            removeThinkingState(assistantElements.messageArticle);

            if (!assistantElements.messageText.textContent) {
                assistantElements.messageText.textContent = 'I could not complete that request. Please try again.';
            }

            setError('I could not complete that request. Please try again.');
            return false;
        } finally {
            setFormBusy(false);
            messageInput.focus();
            scrollHistoryToBottom();
        }
    };

    const autoResizeMessageInput = () => {
        messageInput.style.overflowY = 'hidden';
        messageInput.style.height = 'auto';
        messageInput.style.height = messageInput.scrollHeight + 'px';
        if (messageInput.scrollHeight > messageInput.clientHeight) {
            messageInput.style.overflowY = 'auto';
        }
    };

    messageInput.addEventListener('input', autoResizeMessageInput);

    messageInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            const message = messageInput.value.trim();
            submitMessage(message, true);
        }
    });

    chatForm.addEventListener('submit', async (event) => {
        event.preventDefault();

        const message = messageInput.value.trim();
        await submitMessage(message, true);
    });

    if (summariseButton) {
        summariseButton.addEventListener('click', async () => {
            await submitMessage('End chat', false, true);
        });
    }
}

function initialiseConversationNavigation() {
    // Only activate AJAX navigation when already on a Conversation page —
    // avoids structural mismatches with the Index (new-chat) layout.
    if (!/\/Home\/Conversation\/\d+/i.test(window.location.pathname)) {
        return;
    }

    const sidebar = document.querySelector('.chat-sidebar');
    const conversationInput = document.getElementById('chat-conversation-id');
    if (!sidebar) {
        return;
    }

    // Capture the initial full-page state so back-navigation works correctly.
    history.replaceState(
        { conversationUrl: window.location.pathname },
        document.title,
        window.location.href
    );

    const showLoadingSpinner = (historyElement) => {
        historyElement.innerHTML =
            '<div class="d-flex justify-content-center align-items-center flex-grow-1 py-5">' +
            '<div class="spinner-border text-primary" role="status">' +
            '<span class="sr-only">Loading...</span>' +
            '</div></div>';
    };

    const showLoadError = (historyElement) => {
        historyElement.innerHTML =
            '<div class="d-flex flex-column w-100 mb-3 align-items-start">' +
            '<article class="d-inline-block w-auto border px-4 py-3 shadow-sm bg-white chat-bubble" aria-label="Chat message">' +
            '<div class="mb-0 chat-message-text chat-message-text-assistant" data-chat-role="assistant" data-chat-message-text>' +
            'This conversation could not be loaded. Please try again.' +
            '</div></article></div>';
    };

    const updateSidebarSelection = (pageUrl) => {
        for (const link of sidebar.querySelectorAll('.history-conversation-link')) {
            const href = link.getAttribute('href') ?? '';
            const isSelected = href !== '' && pageUrl.endsWith(href);
            link.classList.toggle('history-conversation-link-selected', isSelected);
            if (isSelected) {
                link.setAttribute('aria-current', 'page');
            } else {
                link.removeAttribute('aria-current');
            }
        }
    };

    const loadConversation = async (pageUrl, partialUrl, title, pushState) => {
        const historyElement = document.getElementById('chat-history');
        if (!historyElement) {
            return;
        }

        const form = document.getElementById('chat-form');
        form.dataset.isLoadingConversation = 'true';
        updateFlagButton();
        showLoadingSpinner(historyElement);

        try {
            const response = await fetch(partialUrl, {
                headers: { 'X-Requested-With': 'XMLHttpRequest' }
            });

            if (!response.ok) {
                showLoadError(historyElement);
                return;
            }

            const html = await response.text();
            historyElement.innerHTML = html;
            const conversationIdElement = historyElement.querySelector('[data-chat-conversation-id]');
            if (conversationInput) {
                conversationInput.value = conversationIdElement?.dataset.chatConversationId ?? '';
                updateFlagButton(conversationIdElement?.dataset.isFlagged === 'true');
            }
            initialiseRenderedChatMessages();
        } catch {
            const el = document.getElementById('chat-history');
            if (el) {
                showLoadError(el);
            }
            return;
        } finally {
            form.dataset.isLoadingConversation = 'false';
            updateFlagButton();
        }

        updateSidebarSelection(pageUrl);

        if (pushState) {
            history.pushState({ conversationUrl: pageUrl }, title, pageUrl);
        }

        document.title = title;
    };

    sidebar.addEventListener('click', async (event) => {
        const link = event.target.closest('.history-conversation-link');
        if (!link) {
            return;
        }

        const href = link.getAttribute('href');
        if (!href) {
            return;
        }

        const match = href.match(/\/Home\/Conversation\/(\d+)/i);
        if (!match) {
            return;
        }

        event.preventDefault();

        const partialUrl = href.replace(/\/Conversation\//i, '/ConversationPartial/');
        const title = link.textContent.trim();

        await loadConversation(href, partialUrl, title, true);
    });

    window.addEventListener('popstate', async (event) => {
        const conversationUrl = event.state?.conversationUrl;
        if (!conversationUrl) {
            return;
        }

        const match = conversationUrl.match(/\/Home\/Conversation\/(\d+)/i);
        if (!match) {
            // Navigated back to a non-conversation state (e.g. Index); let the
            // browser handle the full navigation.
            window.location.assign(conversationUrl);
            return;
        }

        const partialUrl = conversationUrl.replace(/\/Conversation\//i, '/ConversationPartial/');
        await loadConversation(conversationUrl, partialUrl, document.title, false);
    });
}

function initialiseSidebarPersistence() {
    const toggle = document.querySelector('.chat-sidebar-toggle');
    if (!toggle) return;

    toggle.addEventListener('click', () => {
        setTimeout(() => {
            const expanded = toggle.getAttribute('aria-expanded');
            const maxAge = 365 * 24 * 60 * 60;
            document.cookie = `pref_sidebarExpanded=${expanded}; path=/; max-age=${maxAge}; SameSite=Lax`;
        }, 0);
    });
}

function initialiseSidebarHistoryLinkBehaviour() {
    const sidebar = document.querySelector('.chat-sidebar');
    if (!sidebar) return;

    sidebar.addEventListener('click', (event) => {
        const link = event.target.closest('.history-conversation-link');
        if (!link) return;

        const toggle = document.querySelector('.chat-sidebar-toggle');
        if (toggle && window.innerWidth < 992 && toggle.getAttribute('aria-expanded') === 'true') {
            toggle.click();
        }
    });
}

function initialiseNewAskButtonBehaviour() {
    const newAskButtons = document.querySelectorAll('.chat-new-chat-button, .chat-new-chat-button-compact');
    newAskButtons.forEach(button => {
        button.addEventListener('click', () => {
            const toggle = document.querySelector('.chat-sidebar-toggle');
            if (toggle && window.innerWidth < 992 && toggle.getAttribute('aria-expanded') === 'true') {
                const maxAge = 365 * 24 * 60 * 60;
                document.cookie = `pref_sidebarExpanded=false; path=/; max-age=${maxAge}; SameSite=Lax`;
            }
        });
    });
}

const initialiseChatPage = () => {
    initialiseRenderedChatMessages();
    initialiseChatStreaming();
    initialiseConversationFlagging();
    initialiseConversationNavigation();
    initialiseSidebarPersistence();
    initialiseSidebarHistoryLinkBehaviour();
    initialiseNewAskButtonBehaviour();
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseChatPage);
} else {
    initialiseChatPage();
}

window.addEventListener('pageshow', () => {
    initialiseRenderedChatMessages();
});
